import { Link } from "react-router";
import type { DecisionDTO } from "@roomate/core";
import { useIntent, useProjects, useTasks } from "../api.ts";
import { projectColor, projectInitial } from "../lib/project-mark.ts";

/** The small coloured initial that stands for a project everywhere (breadcrumb, Inbox filter, Projects page). */
export function ProjectMark({ id, name, size = 16 }: { id: string; name: string; size?: number }) {
  return (
    <span
      aria-hidden
      data-testid="project-mark"
      className="inline-grid shrink-0 place-items-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, background: projectColor(id), fontSize: Math.round(size * 0.62) }}
    >
      {projectInitial(name)}
    </span>
  );
}

type Crumb = { to: string; label: string } | null;

/** Pure view: project name > #task title (or intent title). Each part is a link; a missing project name falls back to "Project". */
export function DecisionCrumbView({ projectId, projectName, task, intent }: { projectId: string; projectName: string | undefined; task: Crumb; intent: Crumb }) {
  const name = projectName ?? "Project";
  const leaf = task ?? intent;
  const link = "block min-w-0 max-w-[32ch] truncate underline decoration-transparent hover:decoration-rule hover:text-ink";
  return (
    <nav aria-label="Breadcrumb" data-testid="decision-crumb" className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-ink-2">
      <ProjectMark id={projectId} name={name} />
      <Link to={`/projects/${projectId}`} data-testid="decision-crumb-project" className={`${link} font-medium text-ink`}>
        {name}
      </Link>
      {leaf && (
        <>
          <span aria-hidden className="text-ink-3">
            ›
          </span>
          <Link to={leaf.to} data-testid="decision-crumb-leaf" className={link}>
            {leaf.label}
          </Link>
        </>
      )}
    </nav>
  );
}

/** The breadcrumb above a decision's question. Reads the same cached projects/tasks lists the rest of the app does. */
export function DecisionCrumb({ decision }: { decision: Pick<DecisionDTO, "projectId" | "taskId" | "intentId"> }) {
  const projects = useProjects().data;
  const tasks = useTasks().data;
  const intent = useIntent(decision.intentId ?? "", !decision.taskId).data?.intent;
  const t = decision.taskId ? tasks?.find((x) => x.id === decision.taskId) : undefined;
  return (
    <DecisionCrumbView
      projectId={decision.projectId}
      projectName={projects?.find((p) => p.id === decision.projectId)?.name}
      task={decision.taskId ? { to: `/tasks/${decision.taskId}`, label: t ? `#${t.number} ${t.title}` : "Task" } : null}
      intent={!decision.taskId && decision.intentId ? { to: `/intents/${decision.intentId}`, label: intent?.title ?? "Idea" } : null}
    />
  );
}
